import './anfragen.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate, fmtDateTime, fmtKB } from '../../lib/format';
import { eurC } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { AREA_LABEL } from './labels';
import type { KanzleiRequest } from './types';

// Angeforderte Unterlage bearbeiten: Wunsch des Mandanten, Dateien hochladen (mit freiwilliger Info und „Damit ist die
// Anfrage erledigt“), falsche Datei entfernen, Kommentare, „Erledigt“ bzw. „Wieder öffnen“. Öffnen gilt als gesehen.
export function RequestDialog({ id, onClose }: { id: string; onClose(): void }) {
  const toast = useToast();
  const res = useApi<KanzleiRequest>(`/api/kanzlei/anfrage/${id}`, { live: ['anfragen'] });
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const r = res.data;

  // gesehen: beim Öffnen und wenn bei offenem Fenster ein neuer Kommentar des Mandanten ankommt
  const clientComments = r?.comments.filter((c) => c.side === 'client').length ?? 0;
  useEffect(() => {
    api.post(`/api/kanzlei/anfrage/${id}/read`).catch(() => {});
  }, [id, clientComments]);

  const act = async (run: () => Promise<unknown>, msg: string, close = false) => {
    setBusy(true);
    try {
      await run();
      toast(msg);
      if (close) onClose();
      else await res.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={r ? `${r.label}${r.employee ? ` · ${r.employee.name}` : ''}` : 'Anfrage'}
      className="anf"
    >
      {!r ? (
        res.error ? (
          <p className="note">{res.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          <p className="note">
            {r.clientName} · Nr. {r.nr} · angefordert am {fmtDate(r.createdAt)}
            {r.createdBy ? ` von ${r.createdBy}` : ''} · über die App
            {r.price ? ` · ${eurC(r.price)} netto` : ''}
          </p>
          <p className="anf-st">
            {r.status === 'erledigt' ? (
              <span className="pill p-ok">
                erledigt{r.doneAt ? ` am ${fmtDate(r.doneAt)}` : ''}
                {r.doneBy ? ` von ${r.doneBy}` : ''}
              </span>
            ) : (
              <span className="pill p-info">offen · {AREA_LABEL[r.area]}</span>
            )}
          </p>
          {r.art !== r.label && <p className="note anf-art">Gewählt: {r.art}</p>}
          {r.note && (
            <div className="kibox">
              <div>
                <b>Wunsch des Mandanten:</b> „{r.note}“
              </div>
            </div>
          )}
          <Files r={r} reload={res.reload} />
          <div className="anf-done">
            {r.status === 'erledigt' ? (
              r.mine && (
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  onClick={() => void act(() => api.post(`/api/kanzlei/anfrage/${id}/reopen`), 'Anfrage ist wieder offen')}
                >
                  Wieder öffnen
                </button>
              )
            ) : asking ? (
              <DoneForm
                r={r}
                busy={busy}
                cancel={() => setAsking(false)}
                submit={(note) =>
                  act(
                    () => api.post(`/api/kanzlei/anfrage/${id}/done`, note ? { note } : {}),
                    `Erledigt · ${r.contactName} bekommt eine Meldung in der App`,
                    true,
                  )
                }
              />
            ) : (
              <button
                type="button"
                className={`btn sm${r.files.length ? ' dark' : ''}`}
                disabled={busy}
                onClick={() => setAsking(true)}
              >
                {r.files.length ? 'Erledigt' : 'Ohne Datei erledigen'}
              </button>
            )}
          </div>
          <div className="note anf-h">
            <b>Kommentare</b> · sieht {r.contactName} in der App
          </div>
          <div className="thread">
            {r.comments.map((c) => (
              <div className="cm" key={c.id}>
                <b>{c.authorName}</b>
                {c.side === 'client' && (
                  <>
                    {' '}
                    <span className="pill p-info">Mandant</span>
                  </>
                )}{' '}
                <span className="note">{fmtDateTime(c.createdAt)}</span>
                <div>{c.body}</div>
              </div>
            ))}
            <CommentForm r={r} reload={res.reload} />
          </div>
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={onClose}>
              Schließen
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

// Dateien für den Mandanten: Liste (ansehen, entfernen) und Hochladen mit Bestätigung
function Files({ r, reload }: { r: KanzleiRequest; reload(): Promise<void> }) {
  const toast = useToast();
  const pick = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(true);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const open = r.status === 'offen';

  const add = (list: FileList | null) => {
    if (!list?.length) return;
    const known = new Set(files.map((f) => `${f.name}|${f.size}`));
    setFiles([...files, ...Array.from(list).filter((f) => !known.has(`${f.name}|${f.size}`))]);
  };

  const send = async () => {
    setBusy(true);
    const fd = new FormData();
    // zuerst die Angaben, dann die Dateien (so prüft der Server, bevor er speichert)
    fd.append('data', JSON.stringify({ note: note.trim() || undefined, done: open && done }));
    for (const f of files) fd.append('file', f, f.name);
    try {
      const res = await api.post<{ files: number; status: string }>(`/api/kanzlei/anfrage/${r.id}/files`, fd);
      setFiles([]);
      setNote('');
      await reload();
      toast(
        `${res.files === 1 ? 'Datei' : `${res.files} Dateien`} hochgeladen · ${r.contactName} sieht sie in der App${res.status === 'erledigt' ? ' · erledigt' : ''}`,
      );
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (fileId: string, name: string) => {
    setBusy(true);
    try {
      await api.del(`/api/kanzlei/anfrage/${r.id}/file/${fileId}`);
      setRemoving(null);
      await reload();
      toast(`Entfernt: ${name}`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="anf-box">
      <h3>Dateien für {r.contactName}</h3>
      {!r.mine && (
        <p className="note">
          Dateien der {r.area === 'lohn' ? 'Lohnbuchhaltung' : 'Finanzbuchhaltung'} sehen nur die Zuständigen. Hier stehen nur
          Ihre eigenen.
        </p>
      )}
      {r.files.length > 0 ? (
        <div className="ul">
          {r.files.map((f) => (
            <div key={f.id}>
              <span>
                <a href={`/api/kanzlei/anfrage/${r.id}/file/${f.id}`} target="_blank" rel="noopener noreferrer">
                  📄 {f.name}
                </a>
                <span className="sub">
                  {fmtKB(f.size)} · am {fmtDate(f.createdAt)}
                  {f.uploadedBy ? ` von ${f.uploadedBy}` : ''}
                </span>
              </span>
              {removing === f.id ? (
                <span className="anf-ask">
                  <button type="button" className="btn sm dark" disabled={busy} onClick={() => remove(f.id, f.name)}>
                    Ja, entfernen
                  </button>
                  <button type="button" className="btn sm" onClick={() => setRemoving(null)}>
                    Abbrechen
                  </button>
                </span>
              ) : (
                <button type="button" className="del" onClick={() => setRemoving(f.id)} aria-label={`${f.name} entfernen`}>
                  Entfernen
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="note">Noch keine Datei hochgeladen.</p>
      )}
      {files.length ? (
        <div className="confirmup">
          <b>
            {files.length === 1 ? 'Diese Datei' : `Diese ${files.length} Dateien`} an {r.contactName} senden?
          </b>
          <div className="upf">
            {files.map((f, i) => (
              <span key={`${f.name}|${f.size}`}>
                📄 {f.name}{' '}
                <button
                  type="button"
                  className="linkbtn"
                  aria-label={`${f.name} aus der Auswahl nehmen`}
                  onClick={() => setFiles(files.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <label className="anf-note">
            <span className="note">Info an {r.contactName} (freiwillig, kommt als Kommentar)</span>
            <textarea
              rows={2}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z. B. Stand 30.09.2026, wie gewünscht für die Bank."
            />
          </label>
          {open && (
            <label className="anf-chk">
              <input type="checkbox" checked={done} onChange={(e) => setDone(e.target.checked)} />
              Damit ist die Anfrage erledigt
            </label>
          )}
          <div className="bk-actions">
            <button type="button" className="btn sm dark" disabled={busy} onClick={send}>
              {busy ? 'Einen Moment …' : files.length === 1 ? 'Hochladen' : `${files.length} Dateien hochladen`}
            </button>
            <button type="button" className="btn sm" onClick={() => pick.current?.click()}>
              Weitere hinzufügen
            </button>
            <button type="button" className="btn sm" onClick={() => setFiles([])}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`drop${over ? ' over' : ''}`}
          onClick={() => pick.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            add(e.dataTransfer.files);
          }}
        >
          <b>{r.files.length ? 'Weitere Datei hochladen' : 'Unterlage hochladen'}</b>
          <small>PDF oder Foto hierher ziehen oder klicken · mehrere Dateien möglich</small>
        </button>
      )}
      <input
        ref={pick}
        type="file"
        multiple
        accept="application/pdf,image/*"
        hidden
        data-testid="anfrage-pick"
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
    </section>
  );
}

// „Erledigt“ mit freiwilliger kurzer Info an den Mandanten
function DoneForm({ r, busy, cancel, submit }: { r: KanzleiRequest; busy: boolean; cancel(): void; submit(note: string): void }) {
  const [note, setNote] = useState('');
  const send = (e: FormEvent) => {
    e.preventDefault();
    submit(note.trim());
  };
  return (
    <form className="ldone" onSubmit={send}>
      <label htmlFor="ad-note" className="note">
        Kurze Info an {r.contactName} (freiwillig, kommt als Kommentar)
      </label>
      <textarea
        id="ad-note"
        rows={2}
        maxLength={1000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={r.files.length ? 'z. B. Die Bescheinigung liegt in der App.' : 'z. B. Haben wir Ihnen per Post geschickt.'}
        autoFocus
      />
      <div className="row">
        <button type="submit" className="btn sm dark" disabled={busy}>
          Als erledigt melden
        </button>
        <button type="button" className="btn sm" onClick={cancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function CommentForm({ r, reload }: { r: KanzleiRequest; reload(): Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/kanzlei/anfrage/${r.id}/comments`, { body });
      setText('');
      await reload();
      toast(`Kommentar gesendet · ${r.contactName} sieht ihn in der App`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {error && (
        <p className="note anf-err" role="alert">
          {error}
        </p>
      )}
      <form className="cform" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder={`Kommentar an ${r.contactName}, z. B. Für welchen Zeitraum?`}
          aria-label="Kommentar zur Anfrage"
        />
        <button type="submit" className="btn sm" disabled={busy}>
          Senden
        </button>
      </form>
    </>
  );
}
